import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  IconButton,
  Link,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import { ReactComponent as ExpandIcon } from 'new/components/icons/fi-sr-expand-arrows-alt.svg';
import { ReactComponent as CollapseIcon } from 'new/components/icons/fi-sr-collapse-arrows-alt.svg';
import useAppDispatch from 'js/hooks/useAppDispatch';
import { probeGetConnectionTree } from 'js/actions/probe';
import { verifyCredentialFromForm } from 'js/actions';
import { FORM_TYPE_CREATE_DIRECT_FLOW } from 'js/constants/formTypes';
import { FileList } from 'new/components/BreadcrumbTreeView';
import type { TreeNode } from 'new/components/BreadcrumbTreeView';
import { PROBE_GET_TREE_REQUEST } from 'js/constants/actionTypes';
import {
  selectProbeByCredentialId,
  type ProbeRootState,
  type ProbeTreeNode as ProbeNode,
} from 'new/state/probe/selectors/probeTreeSelectors';
import FilePreviewDialog from 'new/components/flows/shared/customProcessor/FilePreviewDialog';
import type { ByoFile } from '../types';
import classes from './InputPanel.module.scss';

export interface ByoFileBrowserProps {
  credentialId: number | string | null | undefined;
  /** What the user is choosing: a single file, or a directory. */
  selectMode?: 'file' | 'directory';
  /** Currently selected path (for highlight). */
  selectedPath?: string | null;
  /** Optionally start navigation inside this folder (e.g. the chosen directory). */
  initialFolder?: string | null;
  /** Called when the user picks a file (file mode) or a folder (directory mode). */
  onSelectFile: (file: ByoFile) => void;
  /** Hide folder rows — a flat file-only listing for the selected folder. */
  filesOnly?: boolean;
  /** Pass-through to FileList: hide the Copy Path button. */
  hideCopyPath?: boolean;
  'data-testid'?: string;
}

const isFolderNode = (n: ProbeNode): boolean =>
  n.type === 'folder' || (!n.type && Array.isArray(n.children)) || n.type === 'directory';

/**
 * File/folder browser for BYO v2.
 *
 * Owns its own navigation (`currentFolder`) reading the normalized probe tree
 * (`state.probe[credId].tree`) — so drill-down and selection are deterministic —
 * and renders the rows with the shared {@link FileList} so it matches the rest
 * of the product (folder/file icons, size/modified metadata, copy-path, preview,
 * hover/selected states). The toolbar (breadcrumb + search + refresh) sits above.
 */
export function ByoFileBrowser({
  credentialId,
  selectMode = 'file',
  selectedPath,
  initialFolder = null,
  onSelectFile,
  filesOnly = false,
  hideCopyPath = false,
  'data-testid': dataTestId = 'byo-file-browser',
}: ByoFileBrowserProps) {
  const dispatch = useAppDispatch();
  const [currentFolder, setCurrentFolder] = useState<string | null>(initialFolder);
  const [search, setSearch] = useState('');
  const [fullscreen, setFullscreen] = useState(false);
  // Probe tree key (not the display fullPath): the sample API needs the id for ID-based connectors.
  const [previewPath, setPreviewPath] = useState<string | null>(null);
  // Confirmed live 2026-08-21: a source configured with a folder path that
  // was never discovered by walking the tree UI itself (e.g. typed/pasted
  // during source setup, then jumped straight to here as `initialFolder`)
  // can come back from the probe without ever getting marked `scanned` —
  // the reducer only marks a node scanned if it already has a tree entry,
  // which a never-walked deep path doesn't. `tree[initialFolder]?.scanned`
  // then stays permanently false, and the "fetch it" effects below re-fire
  // on every `treeLastUpdatedAt` tick forever — an actual infinite request
  // loop, not just a slow load. One attempt per distinct path is enough:
  // either it resolves, or it doesn't and re-dispatching the identical
  // request won't change that.
  const attemptedPathsRef = useRef<Set<string>>(new Set());

  const probeState = useSelector((s: ProbeRootState) => selectProbeByCredentialId(s, credentialId));
  const isLoading = useSelector(
    (s: { request?: Record<string, unknown> }) => !!s.request?.[PROBE_GET_TREE_REQUEST],
  );

  const tree: Record<string, ProbeNode> = probeState?.tree ?? {};
  const treeError: string | null = probeState?.error ?? null;

  const listRoot = useCallback(() => {
    if (credentialId == null) return;
    // Manual refresh is the user's explicit "try again" — give
    // attemptedPathsRef's one-shot guard (see its doc above) a clean slate.
    attemptedPathsRef.current.clear();
    const probeRoot = () => dispatch(probeGetConnectionTree(credentialId, true, null) as any);
    Promise.resolve(
      dispatch(verifyCredentialFromForm(FORM_TYPE_CREATE_DIRECT_FLOW, credentialId) as any),
    )
      .then(probeRoot)
      .catch(probeRoot);
  }, [dispatch, credentialId]);

  useEffect(() => {
    setCurrentFolder(initialFolder ?? null);
    if (credentialId == null) return;
    if (!probeState?.treeLastUpdatedAt) {
      listRoot();
    } else if (
      initialFolder &&
      (!tree[initialFolder]?.scanned || tree[initialFolder]?.children === undefined) &&
      !attemptedPathsRef.current.has(initialFolder)
    ) {
      attemptedPathsRef.current.add(initialFolder);
      dispatch(
        probeGetConnectionTree(credentialId, false, {
          path: initialFolder,
          fullPath: initialFolder,
          type: 'folder',
        } as any) as any,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [credentialId, initialFolder]);

  // After the root tree loads, ensure initialFolder is also fetched.
  // The effect above misses this case because treeLastUpdatedAt didn't exist on mount.
  useEffect(() => {
    if (!probeState?.treeLastUpdatedAt) return;
    if (!initialFolder || credentialId == null) return;
    if (tree[initialFolder]?.scanned && tree[initialFolder]?.children !== undefined) return;
    if (attemptedPathsRef.current.has(initialFolder)) return;
    attemptedPathsRef.current.add(initialFolder);
    dispatch(
      probeGetConnectionTree(credentialId, false, {
        path: initialFolder,
        fullPath: initialFolder,
        type: 'folder',
      } as any) as any,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [probeState?.treeLastUpdatedAt]);

  const openFolder = useCallback(
    (path: string, scanned?: boolean) => {
      setCurrentFolder(path);
      setSearch('');
      if (!scanned && credentialId != null) {
        dispatch(
          probeGetConnectionTree(credentialId, false, {
            path,
            fullPath: path,
            type: 'folder',
          } as any) as any,
        );
      }
    },
    [dispatch, credentialId],
  );

  // Items to display, mapped to the FileList TreeNode shape.
  const data: TreeNode[] = useMemo(() => {
    // At root → top-level nodes. Inside a folder → ONLY that folder's children
    // (empty while its probe is in flight) — never fall back to root, which is
    // what made the list and breadcrumb drift out of sync on deep navigation.
    const childPaths = currentFolder
      ? ((tree[currentFolder]?.children as string[]) ?? [])
      : Object.keys(tree).filter((k) => tree[k]?.parent == null);
    const nodes = childPaths.map((p) => tree[p]).filter(Boolean) as ProbeNode[];
    return nodes
      .map((n) => ({
        id: n.fullPath ?? n.path ?? n.id,
        name: n.name,
        displayName: n.displayName ?? n.name,
        type: isFolderNode(n) ? 'folder' : 'file',
        path: n.fullPath ?? n.path ?? n.id,
        size: n.size,
        updatedAt: n.updatedAt,
        mimeType: n.mimeType,
        // keep the tree key for navigation
        _treeKey: n.path,
        _scanned: n.scanned,
      }))
      .filter((n) => !filesOnly || n.type !== 'folder')
      .sort((a, b) => {
        if (a.type !== b.type) return a.type === 'folder' ? -1 : 1;
        return (a.displayName || a.name).localeCompare(b.displayName || b.name);
      }) as unknown as TreeNode[];
  }, [tree, currentFolder, filesOnly]);

  const crumbs: ProbeNode[] = useMemo(() => {
    const trail: ProbeNode[] = [];
    let p: string | null | undefined = currentFolder;
    while (p && tree[p]) {
      trail.unshift(tree[p]);
      p = tree[p].parent;
    }
    return trail;
  }, [currentFolder, tree]);

  const onNodeSelected = useCallback(
    (node: any) => {
      // Folders always NAVIGATE (drill in) — directory selection is the explicit
      // "Use this directory" action below, so clicking a folder is never ambiguous.
      if (node.type === 'folder') {
        openFolder(node._treeKey, node._scanned);
        return;
      }
      // Files are selectable in file mode only.
      if (selectMode === 'file') {
        onSelectFile({
          name: node.displayName ?? node.name,
          path: node.path,
          size: node.size,
          isFolder: false,
        });
      }
    },
    [onSelectFile, openFolder, selectMode],
  );

  const onNodeExpandClicked = useCallback(
    (node: any) => openFolder(node._treeKey, node._scanned),
    [openFolder],
  );

  // Directory mode: select the folder the user is currently viewing.
  const currentNode = currentFolder ? tree[currentFolder] : null;
  const useThisDirectory = () => {
    if (!currentNode) return;
    onSelectFile({
      name: currentNode.displayName ?? currentNode.name,
      path: currentFolder as string,
      isFolder: true,
    });
  };
  const isSelectedDir =
    selectMode === 'directory' && !!currentFolder && selectedPath === currentFolder;

  if (credentialId == null) {
    return (
      <Box data-testid={`${dataTestId}-no-credential`}>
        <Alert severity="info">Select a credential above to browse files.</Alert>
      </Box>
    );
  }

  const isEmpty = data.length === 0;

  return (
    <Box
      data-testid={dataTestId}
      className={fullscreen ? classes.browserFullscreen : undefined}
      sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}
    >
      {/* Toolbar: row 1 = breadcrumb + refresh + expand; row 2 = search.
          Stacking keeps it readable at narrow widths (compact rails). */}
      <Box className={classes.browserToolbar}>
        <Box className={classes.browserToolbarRow}>
          <Breadcrumbs sx={{ flex: 1, minWidth: 0 }} maxItems={4}>
            <Link
              component="button"
              type="button"
              underline="hover"
              color="inherit"
              onClick={() => {
                setCurrentFolder(null);
                setSearch('');
              }}
              sx={{ display: 'flex', alignItems: 'center' }}
              data-testid={`${dataTestId}-home`}
            >
              <HomeIcon fontSize="small" />
            </Link>
            {crumbs.map((c, i) =>
              i === crumbs.length - 1 ? (
                <Typography key={c.path} variant="body2" color="text.primary" noWrap>
                  {c.displayName ?? c.name}
                </Typography>
              ) : (
                <Link
                  key={c.path}
                  component="button"
                  type="button"
                  underline="hover"
                  color="inherit"
                  variant="body2"
                  onClick={() => openFolder(c.path, c.scanned)}
                >
                  {c.displayName ?? c.name}
                </Link>
              ),
            )}
          </Breadcrumbs>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={listRoot} data-testid={`${dataTestId}-refresh`}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={fullscreen ? 'Collapse' : 'Expand'}>
            <IconButton
              size="small"
              onClick={() => setFullscreen((v) => !v)}
              data-testid={`${dataTestId}-expand`}
              aria-label={fullscreen ? 'Collapse' : 'Expand'}
            >
              {fullscreen ? (
                <CollapseIcon className={classes.browserExpandIcon} />
              ) : (
                <ExpandIcon className={classes.browserExpandIcon} />
              )}
            </IconButton>
          </Tooltip>
        </Box>
        <TextField
          size="small"
          placeholder="Find in this folder"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon fontSize="small" sx={{ mr: 0.5, opacity: 0.6 }} />,
          }}
          fullWidth
          inputProps={{ 'data-testid': `${dataTestId}-search` }}
        />
      </Box>

      {treeError ? (
        <Alert severity="error" sx={{ m: 1 }} data-testid={`${dataTestId}-error`}>
          {treeError}
        </Alert>
      ) : null}

      {/* Rows rendered with the shared FileList — matches the product theme.
          FileList uses react-virtuoso (height:100%), so the parent must have a
          resolved height. */}
      <Box
        sx={{ flex: 1, minHeight: 200, '& > div': { height: '100%' } }}
        data-testid={`${dataTestId}-list`}
      >
        <FileList
          data={data}
          selectedNodeId={selectedPath ?? undefined}
          isLoadingData={isLoading && isEmpty}
          selectable
          enableFileSelection={selectMode === 'file'}
          searchValue={search}
          onClearSearch={() => setSearch('')}
          onNodeSelected={onNodeSelected}
          onNodeExpandClicked={onNodeExpandClicked}
          onNodePreview={(node: any) => setPreviewPath(node._treeKey)}
          hideCopyPath={hideCopyPath}
        />
      </Box>

      {/* Directory mode: explicit "use the folder you're viewing" action. */}
      {selectMode === 'directory' ? (
        <Box className={classes.browserFooter}>
          <Typography variant="caption" color="text.secondary" sx={{ flex: 1, minWidth: 0 }} noWrap>
            {currentFolder ? `Folder: ${currentFolder}` : 'Open a folder, then select it'}
          </Typography>
          <Button
            size="small"
            variant={isSelectedDir ? 'outlined' : 'contained'}
            color={isSelectedDir ? 'success' : 'primary'}
            disabled={!currentFolder}
            startIcon={isSelectedDir ? <CheckCircleIcon fontSize="small" /> : undefined}
            onClick={useThisDirectory}
            data-testid={`${dataTestId}-use-directory`}
          >
            {isSelectedDir ? 'Selected' : 'Use this directory'}
          </Button>
        </Box>
      ) : null}

      <FilePreviewDialog
        open={previewPath != null}
        onClose={() => setPreviewPath(null)}
        credentialId={credentialId}
        path={previewPath}
      />
    </Box>
  );
}

export default ByoFileBrowser;
